import React, { useCallback, type MouseEvent } from 'react';
import { styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { ScreenReaderText } from '@atlassian/jira-accessibility/src/common/ui/screenreader-text/index.tsx';
import type { ExternalAction } from '@atlassian/jira-issue-view-store/src/actions/external-actions.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { ScreenReaderKey } from '../../styled.tsx';
import { SCREEN_READER_KEY_TEST_ID, KEY_CONTAINER_TEST_ID } from '../constants.tsx';
import { useCardListContext } from '../../../../../services/card-list-context/index.tsx';
import KeyWithAnalytics from './key-with-analytics/index.tsx';

export type Props = {
	isDone: boolean;
	cardKey: string;
	issueUrl: string;
	summary: string;
	highlightText: string | null;
	onClick: (
		withCmd: boolean,
		withShift: boolean,
		source: 'mouse' | 'keyboard',
		viaCheckbox?: boolean,
		externalAction?: ExternalAction,
	) => void;
};

const stopPropagation = (event: React.MouseEvent<HTMLDivElement>) => event.stopPropagation();

export const Key = ({
	isDone,
	cardKey,
	issueUrl,
	summary,
	highlightText,
	onClick: cardOnClick,
}: Props) => {
	const onKeyClick = useCallback(
		(e: MouseEvent) => {
			const withCmd = e.ctrlKey || e.metaKey;

			if (!withCmd) {
				e.preventDefault();
				cardOnClick(false, false, 'mouse');
			}
		},
		[cardOnClick],
	);

	const { isDisabled = false } = isVisualRefreshEnabled()
		? // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useCardListContext()
		: {};

	// When the ScreenReaderKey is focused, it becomes visible and its sibling
	// Key is hidden. This allows the key to behave as a regular link for keyboard
	// users without compromising dnd functionality for mouse users.
	// Previously, the issue page could not be accessed directly via keyboard.
	return (
		<KeyContainer data-testid={KEY_CONTAINER_TEST_ID} onContextMenu={stopPropagation}>
			<ScreenReaderKey
				href={issueUrl}
				isDone={isDone}
				data-testid={SCREEN_READER_KEY_TEST_ID}
				tabIndex={isDisabled && fg('tanuki_a11y_q4') ? -1 : undefined}
			>
				{cardKey}
				<ScreenReaderText>{summary}</ScreenReaderText>
			</ScreenReaderKey>
			<KeyWithAnalytics
				highlightText={highlightText}
				issueUrl={issueUrl}
				isDone={isDone}
				issueKey={cardKey}
				onClick={onKeyClick}
			/>
		</KeyContainer>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const KeyContainer = styled.div({
	position: 'relative',
	zIndex: 1 /* surface the element above the interaction layer so tooltips can be triggered */,

	paddingRight: token('space.100'),
	gridColumn: 'issue-key / span 1',
	gridRow: 'card-detail / span 1',
});
